import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Package } from "lucide-react";
import { listIndustries, listProducts } from "../lib/portfolio";
import ShareButton from "../components/portfolio/ShareButton";

const Portfolio = () => {
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);
  const [view, setView] = useState("industry"); // 'industry' | 'product'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ind, prod] = await Promise.all([listIndustries(), listProducts()]);
        setIndustries(ind.items || []);
        setProducts(prod.items || []);
      } catch (e) {
        toast.error("Could not load portfolio");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Industries with photos first
  const sortedIndustries = [...industries].sort(
    (a, b) => (b.photo_count || 0) - (a.photo_count || 0)
  );

  // Group products by category for the alt view (preserves insertion order, which
  // for a JS object iterating string keys is OK in modern engines)
  const productsByCategory = products.reduce((acc, p) => {
    const c = p.category || "Other";
    (acc[c] = acc[c] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ background: "var(--ups-paper)" }}>
      <header
        className="sticky top-0 z-20 border-b"
        style={{ background: "#fff", borderColor: "var(--ups-border)" }}
        data-testid="portfolio-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] hover:opacity-70 transition"
            data-testid="portfolio-back-home"
          >
            <ArrowLeft size={14} /> Home
          </button>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex text-xs font-mono text-[color:var(--ups-subtext)] items-center gap-1.5">
              <Sparkles size={11} /> Real work · real customers
            </div>
            <ShareButton testid="portfolio-share-btn" label="Share portfolio link" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] mb-2">
            Portfolio
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight"
            data-testid="portfolio-title"
          >
            See what we<br />actually print.
          </h1>
          <p className="mt-4 text-base text-[color:var(--ups-subtext)] max-w-2xl">
            Browse by industry or by product — every photo is real work from a
            local business. Click any tile to dig in.
          </p>

          {/* Browse mode toggle */}
          <div
            className="mt-6 inline-flex items-center rounded-sm overflow-hidden border-2"
            style={{ borderColor: "var(--ups-ink)" }}
            data-testid="browse-mode-toggle"
          >
            <button
              onClick={() => setView("industry")}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition ${
                view === "industry"
                  ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]"
                  : "bg-white hover:bg-[color:var(--ups-muted)]"
              }`}
              data-testid="browse-by-industry"
            >
              By industry
            </button>
            <button
              onClick={() => setView("product")}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition flex items-center gap-1.5 ${
                view === "product"
                  ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]"
                  : "bg-white hover:bg-[color:var(--ups-muted)]"
              }`}
              data-testid="browse-by-product"
            >
              <Package size={12} /> By product
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[color:var(--ups-subtext)]">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading the gallery…
          </div>
        ) : view === "industry" ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="industry-grid"
          >
            {sortedIndustries.map((ind) => (
              <button
                key={ind.slug}
                onClick={() => navigate(`/portfolio/${ind.slug}`)}
                className="group text-left bg-white rounded-sm overflow-hidden border-2 hover:-translate-y-1 transition-transform"
                style={{ borderColor: "var(--ups-ink)", boxShadow: "5px 5px 0 #FFB500" }}
                data-testid={`industry-card-${ind.slug}`}
              >
                <div
                  className="aspect-[4/3] relative bg-stone-200 overflow-hidden"
                  style={{
                    backgroundImage: ind.hero_thumb_url ? `url("${ind.hero_thumb_url}")` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {!ind.hero_thumb_url && (
                    <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-30">
                      {ind.emoji || "📸"}
                    </div>
                  )}
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, transparent 0%, rgba(28,25,23,0.6) 100%)" }}
                  />
                  {ind.photo_count > 0 && (
                    <span
                      className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm"
                      style={{ background: "#FFB500", color: "var(--ups-ink)" }}
                    >
                      {ind.photo_count} {ind.photo_count === 1 ? "photo" : "photos"}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xl shrink-0">{ind.emoji}</span>
                      <h3 className="font-display text-lg font-bold leading-tight truncate">
                        {ind.label}
                      </h3>
                    </div>
                    <ArrowRight
                      size={16}
                      className="shrink-0 transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-[color:var(--ups-subtext)] border-2 border-dashed rounded-sm"
            style={{ borderColor: "var(--ups-border)" }}
            data-testid="products-empty"
          >
            No products with photos yet — once photos are tagged with product
            tags they'll appear here grouped by category.
          </div>
        ) : (
          // ── Browse by Product — grouped by category ──────────────────
          <div className="space-y-10" data-testid="product-grid">
            {Object.entries(productsByCategory).map(([cat, items]) => (
              <section key={cat}>
                <h2 className="text-[11px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] mb-3">
                  {cat}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {items.map((p) => (
                    <button
                      key={p.slug}
                      onClick={() => navigate(`/portfolio/product/${p.slug}`)}
                      className="group text-left bg-white rounded-sm overflow-hidden border-2 hover:-translate-y-0.5 transition-transform"
                      style={{ borderColor: "var(--ups-ink)" }}
                      data-testid={`product-card-${p.slug}`}
                    >
                      <div
                        className="aspect-square relative bg-stone-200 overflow-hidden"
                        style={{
                          backgroundImage: p.hero_thumb_url ? `url("${p.hero_thumb_url}")` : undefined,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        <div
                          className="absolute inset-0"
                          style={{ background: "linear-gradient(180deg, transparent 50%, rgba(28,25,23,0.65) 100%)" }}
                        />
                        <span
                          className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm"
                          style={{ background: "#FFB500", color: "var(--ups-ink)" }}
                        >
                          {p.photo_count}
                        </span>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-display text-[15px] font-bold leading-tight truncate">{p.name}</h3>
                          <ArrowRight size={14} className="shrink-0 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Portfolio;
